import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRecoilValue } from "recoil";
import {
  SUBSCRIPTION_PAUSE_DETAIL_ENDPOINT,
  SUBSCRIPTION_PAUSE_DOMAIN,
  SubscriptionPauseRequest,
  SubscriptionPauseType,
  subscriptionPauseApi,
} from "api/subscriptionPauseApi";
import { USER_DETAIL_ENDPOINT, USER_DOMAIN } from "api/userApi";
import { axiosInstance } from "api/instance";
import { userAtom } from "recoil/userAtom";
import { captureException } from "utils/sentry";

const PAUSE_QUERY_KEY = [
  SUBSCRIPTION_PAUSE_DOMAIN,
  SUBSCRIPTION_PAUSE_DETAIL_ENDPOINT,
];

// 일시정지 현황 + 현재 예약 조회 (server state)
export function useSubscriptionPause() {
  const user = useRecoilValue(userAtom);

  return useQuery(PAUSE_QUERY_KEY, async () => subscriptionPauseApi.get(), {
    enabled:
      !!axiosInstance.defaults.headers["X-Auth-Token"] && !!user?.accessToken,
    refetchOnWindowFocus: false,
    select: (resp): SubscriptionPauseType | undefined => resp?.result,
  });
}

// 예약 생성/변경/취소 후 현황 + 유저(만료일 연장) 무효화
function useInvalidatePause() {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries(PAUSE_QUERY_KEY);
    queryClient.invalidateQueries([USER_DOMAIN, USER_DETAIL_ENDPOINT]);
  };
}

export function useCreateSubscriptionPause() {
  const invalidate = useInvalidatePause();
  return useMutation(
    (body: SubscriptionPauseRequest) => subscriptionPauseApi.create(body),
    {
      onSuccess: () => invalidate(),
      onError: (error: unknown) => {
        captureException(error, {
          tags: { action: "subscription_pause_create" },
        });
      },
    }
  );
}

export function useUpdateSubscriptionPause() {
  const invalidate = useInvalidatePause();
  return useMutation(
    (body: SubscriptionPauseRequest) => subscriptionPauseApi.update(body),
    {
      onSuccess: () => invalidate(),
      onError: (error: unknown) => {
        captureException(error, {
          tags: { action: "subscription_pause_update" },
        });
      },
    }
  );
}

export function useCancelSubscriptionPause() {
  const invalidate = useInvalidatePause();
  return useMutation(() => subscriptionPauseApi.cancel(), {
    onSuccess: () => invalidate(),
    onError: (error: unknown) => {
      captureException(error, {
        tags: { action: "subscription_pause_cancel" },
      });
    },
  });
}
